import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { createApiClient, type ApiClient } from './client';
import type { ContractProxy, KeelContract, ProposedContract } from './contract';
import type { AppRouter } from './router-type';
import { planLimitOf, useUpgrade } from '@/lib/plan';

const trpcContext = createTRPCContext<AppRouter>();
type IsAny<T> = 0 extends 1 & T ? true : false;
/** The real inferred proxy once `AppRouter` is a real type; the hand-written contract until then. */
export type KeelTRPC = IsAny<AppRouter> extends true ? ContractProxy<KeelContract> : ReturnType<typeof trpcContext.useTRPC>;

export const { TRPCProvider, useTRPCClient } = trpcContext;
/** Typed tRPC options proxy: `useQuery(trpc.engagement.homeSummary.queryOptions({ entityId }))`. */
export const useTRPC = trpcContext.useTRPC as unknown as () => KeelTRPC;
/** Same runtime proxy, typed for procedures still in docs/proposals (see `ProposedContract`). */
export const useProposedTRPC = trpcContext.useTRPC as unknown as () => ContractProxy<ProposedContract>;

/** Human message for any error thrown by a tRPC call. */
export const errorMessage = (err: unknown): string => {
  if (err instanceof TRPCClientError) return err.message || 'Something went wrong talking to the server.';
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
};

/** Error code from a TRPCClientError (`UNAUTHORIZED`, `FORBIDDEN`, …), if any. */
export const errorCode = (err: unknown): string | undefined => {
  if (!(err instanceof TRPCClientError)) return undefined;
  const data: unknown = err.data;
  if (data && typeof data === 'object' && 'code' in data && typeof data.code === 'string') return data.code;
  return undefined;
};

export const isAuthError = (err: unknown): boolean => errorCode(err) === 'UNAUTHORIZED';

/**
 * Query meta flags: `{ silent: true }` suppresses the global error toast (the screen handles the error itself).
 * Mutations show a toast on error unless `meta.silent` is set.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

export const makeQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => !isAuthError(err) && errorCode(err) !== 'FORBIDDEN' && errorCode(err) !== 'TOO_MANY_REQUESTS' && errorCode(err) !== 'NOT_FOUND' && count < 2,
      },
    },
    queryCache: new QueryCache({
      onError: (err, query) => {
        // a locked screen shows LockedPanel; popping the upgrade sheet on page load would nag
        if (planLimitOf(err)) return;
        if (query.meta?.silent || isAuthError(err)) return;
        toast.error(errorMessage(err), { id: `q:${query.queryHash}` });
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        const need = planLimitOf(err);
        if (need) return useUpgrade.getState().open(need);
        if (mutation.meta?.silent || mutation.options.onError) return;
        toast.error(errorMessage(err));
      },
    }),
  });

export interface ApiProviderProps {
  readonly children: ReactNode;
  /** Override for tests. */
  readonly client?: ApiClient;
  readonly queryClient?: QueryClient;
}

/** Provides TanStack Query + the typed tRPC options proxy (`useTRPC()`). */
export const ApiProvider = ({ children, client, queryClient }: ApiProviderProps) => {
  const [qc] = useState(() => queryClient ?? makeQueryClient());
  const [tc] = useState(() => client ?? createApiClient());
  return (
    <QueryClientProvider client={qc}>
      <TRPCProvider trpcClient={tc} queryClient={qc}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
};
